// frontend/src/components/Common/Comments/CommentsButton.jsx
//
// Self-contained "comments" icon button + CommentsPanel.
//
// Props:
//   entityType      – parent entity, e.g. 'sample_order_header'
//   entityId        – parent internal numeric id
//   subEntityType   – optional child, e.g. 'line_operation'
//   subEntityId     – optional child id (null → button disabled when subEntityType is set)
//   subEntityLabel  – reference text stored with new comments ("CORTE · Cortar · L3 · 69140")
//   count           – optional { total, unread } supplied by a parent (batch load).
//                     When omitted the button fetches its own counts.
//   onCountsChange  – optional (subEntityId ?? entityId, { total, unread }) => void,
//                     called after the panel closes so a parent can refresh its badges.
//   className, size, disabledTitle – presentation

import React, { useCallback, useEffect, useState } from 'react';
import { FiMessageSquare } from 'react-icons/fi';
import { commentsAPI } from '../../../api/client';
import { useTranslation } from '../../../context/TranslationContext';
import CommentsPanel from './CommentsPanel';
import './Comments.css';

const CommentsButton = ({
  entityType,
  entityId,
  subEntityType,
  subEntityId,
  subEntityLabel,
  count,
  onCountsChange,
  className = '',
  size = 16,
  disabledTitle,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [ownCounts, setOwnCounts] = useState({ total: 0, unread: 0 });

  const controlled = count !== undefined;
  const counts = controlled ? (count || { total: 0, unread: 0 }) : ownCounts;
  const sub = subEntityType ? { subEntityType, subEntityId } : undefined;

  const loadCounts = useCallback(async () => {
    if (!entityId || (subEntityType && !subEntityId)) return;
    try {
      const [listRes, unreadRes] = await Promise.all([
        commentsAPI.getComments(entityType, entityId, sub),
        commentsAPI.getUnreadCount(entityType, entityId, sub),
      ]);
      const next = {
        total:  listRes.data?.success ? (listRes.data.data?.length || 0) : 0,
        unread: unreadRes.data?.success ? (unreadRes.data.unreadCount || 0) : 0,
      };
      if (!controlled) setOwnCounts(next);
      onCountsChange?.(subEntityId ?? entityId, next);
    } catch (err) {
      console.error('[CommentsButton] count load failed:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId, subEntityType, subEntityId, controlled, onCountsChange]);

  useEffect(() => {
    if (!controlled) loadCounts();
  }, [controlled, loadCounts]);

  const disabled = !entityId || (!!subEntityType && !subEntityId);

  return (
    <>
      <button
        type="button"
        className={`comments-btn ${counts.total > 0 ? 'comments-btn--has' : ''} ${className}`}
        disabled={disabled}
        title={disabled
          ? (disabledTitle || t('comments.save_first', 'Guarde primeiro para poder comentar'))
          : t('comments.view_comments', 'Comentários')}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <FiMessageSquare size={size} />
        {counts.unread > 0 ? (
          <span className="comments-btn-badge comments-btn-badge--unread">{counts.unread}</span>
        ) : counts.total > 0 ? (
          <span className="comments-btn-badge">{counts.total}</span>
        ) : null}
      </button>

      {!disabled && (
        <CommentsPanel
          entityType={entityType}
          entityId={entityId}
          subEntityType={subEntityType}
          subEntityId={subEntityId}
          subEntityLabel={subEntityLabel}
          isOpen={open}
          nested
          onClose={() => {
            setOpen(false);
            loadCounts();
          }}
        />
      )}
    </>
  );
};

export default CommentsButton;